/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/media-has-caption */
import { Button, Card, CardContent, CardMedia, CircularProgress, darken, Icon, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { capitalizeAll } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { ActlyzerCameraInfo, ActlyzerCameraStreamState, getStreamingStateDisplayName } from '.';
import { VideoFrame } from '../..';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { useSelectProject } from '../../../../../utils/dataStateHooks';
import { getDateTimeDisplay } from '../../../../containerUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            flexDirection: 'column',
            position     : 'relative',

            // When state is PLAYING or PAUSE we will hide the overlay and show it when the user mouse over the video
            // For all other states, we will always show the overlay
            [`&.state-${ActlyzerCameraStreamState.STREAMING}, &.state-${ActlyzerCameraStreamState.ON_PAUSE}`]: {
                '& $controlOverlay': {
                    display: 'none',
                },

                '&:hover': {
                    '& $controlOverlay': {
                        display: 'flex',
                    },
                },
            },

            [`&.state-${ActlyzerCameraStreamState.STREAMING}`]: {
                '& $cameraInfo': {
                    '& .camera-icon': {
                        color    : theme.palette.success.light,
                        animation: `$animate-flashing 1000ms infinite ${theme.transitions.easing.easeInOut}`,
                    },
                },
            },
        },

        cardMedia: {
            height: '100%',
            flex  : '1',
        },

        videoContainer: {
            height  : '100%',
            position: 'relative',
        },

        videoImage: {
            width     : '100%',
            objectFit : 'contain',
            display   : 'block',
            zIndex    : 1,
            minHeight : 200,
            background: darken(theme.palette.background.paper, 0.1),
        },

        controlOverlay: {
            display       : 'flex',
            width         : '100%',
            height        : '100%',
            top           : 0,
            left          : 0,
            position      : 'absolute',
            background    : 'rgba(0, 0, 0, 0.6)',
            color         : '#ffffff',
            zIndex        : 3,
            alignItems    : 'center',
            justifyContent: 'center',
            flexDirection : 'column',
        },

        controlBar: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
        },

        controlButton: {
            color    : '#ffffff',
            margin   : theme.spacing(3),
            '& .icon': {
                fontSize: 75,
            },
        },

        waitingMessageContainer: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            marginBottom  : theme.spacing(2),
            '& .icon'     : {
                fontSize: 50,
            },
            '& .message': {
                marginLeft: theme.spacing(1),
            },
        },

        cardContent: {
            display       : 'flex',
            width         : '100%',
            height        : '100%',
            top           : 0,
            left          : 0,
            position      : 'absolute',
            zIndex        : 2,
            alignItems    : 'center',
            justifyContent: 'center',
            flexDirection : 'column',
        },

        cameraInfo: {
            position      : 'absolute',
            top           : theme.spacing(1),
            left          : theme.spacing(1),
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            fontSize      : 'larger',
            background    : 'rgba(0, 0, 0, 0.6)',
            color         : '#ffffff',
            padding       : theme.spacing(0, 1),

            '& .camera-icon': {
            },

            '& .camera-id': {
                marginRight: theme.spacing(1),
                fontWeight : 'bold',
                lineHeight : '1em',
            },
        },

        timestamp: {
            position  : 'absolute',
            padding   : theme.spacing(0, 1),
            top       : theme.spacing(1),
            right     : theme.spacing(1),
            background: 'rgba(0, 0, 0, 0.6)',
            color     : '#ffffff',
            fontWeight: 'bold',
        },

        visitorCountContainer: {
            position      : 'absolute',
            bottom        : theme.spacing(1),
            right         : theme.spacing(1),
            color         : '#ffffff',
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            fontWeight    : 'bold',
            background    : 'rgba(0, 0, 0, 0.6)',
            padding       : theme.spacing(0, 1),

            '& .value': {
                fontSize  : 30,
                lineHeight: '1em',
            },

            '& .label': {
            },
        },

        '@keyframes animate-flashing': {
            '0%': {
                opacity: 1,
            },
            '20%': {
                opacity: 0,
            },
        },

    };
}, { name: 'ActlyzerCameraCard', index: 1 });



export interface ActlyzerCameraCardProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly frameRate?: number | undefined;
    readonly cameraInfo: ActlyzerCameraInfo;
    readonly showVisitorsCount?: boolean | undefined;
    readonly onStartStreaming: ()=> void;
    readonly onStopStreaming: ()=> void;
    readonly onPauseStreaming: ()=> void;
    readonly onUnpauseStreaming: ()=> void;
    readonly onCancelStreamingRequest: ()=> void;
}



/**
 * This is a component that show 1 Camera details based on the provided cameraInfo. It will use the VideoStreamPlayer component to stream
 * videos from the camera
 */
export const ActlyzerCameraCard: React.FC<ActlyzerCameraCardProps> = (props: ActlyzerCameraCardProps) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // Get a reference to the camera's videoFramesQueue and state.
    const { streamingState } = props.cameraInfo;

    const [currentVideoFrame, setCurrentVideoFrame] = useState<VideoFrame | undefined>();

    /**
     * This useEffect is used for monitoring the "currentVideoFrame" change event. If the "currentVideoFrame" changed then we call
     * "setCurrentVideoFrame" to replace the local "currentVideoFrame" with the new "currentVideoFrame". However, we will only replace
     * the local frame if the current streaming state IS NOT ON_PAUSE
     */
    useEffect(() => {
        if (streamingState !== ActlyzerCameraStreamState.ON_PAUSE) {
            setCurrentVideoFrame(props.cameraInfo.currentVideoFrame);
        }
    }, [props.cameraInfo.currentVideoFrame, streamingState]);


    const createOverlayContent = useCallback(() => {
        if (
            streamingState === ActlyzerCameraStreamState.NOT_STREAMING
            || streamingState === ActlyzerCameraStreamState.STREAMING
            || streamingState === ActlyzerCameraStreamState.ON_PAUSE
        ) {
            // Create a control bar with PLAY, PAUSE, and STOP buttons depending on the current state
            return (
                <div className={classes.controlBar}>
                    {/* Show PLAY button if the camera is NOT streaming or it is in PAUSE state */}
                    {(streamingState === ActlyzerCameraStreamState.NOT_STREAMING || streamingState === ActlyzerCameraStreamState.ON_PAUSE) && (
                        <>
                            {/* Always show the PLAY button which can be used to START streaming or continue STREAMING */}
                            <IconButton
                                className={classes.controlButton}
                                onClick={() => {
                                    if (streamingState === ActlyzerCameraStreamState.NOT_STREAMING) {
                                        props.onStartStreaming();
                                    } else {
                                        props.onUnpauseStreaming();
                                    }
                                }}
                            >
                                <Icon className="icon">play_arrow</Icon>
                            </IconButton>

                            {/* ONLY if the video is in PAUSE state, add the STOP button to allow the user to stop streaming */}
                            {streamingState === ActlyzerCameraStreamState.ON_PAUSE && (
                                <IconButton
                                    className={classes.controlButton}
                                    onClick={props.onStopStreaming}
                                >
                                    <Icon className="icon">stop</Icon>
                                </IconButton>
                            )}
                        </>
                    )}
                    {/* Add PAUSE and STOP button if the camera is in STREAMING mode */}
                    {streamingState === ActlyzerCameraStreamState.STREAMING && (
                        <>
                            <IconButton
                                className={classes.controlButton}
                                onClick={props.onPauseStreaming}
                            >
                                <Icon className="icon">pause</Icon>
                            </IconButton>
                            <IconButton
                                className={classes.controlButton}
                                onClick={props.onStopStreaming}
                            >
                                <Icon className="icon">stop</Icon>
                            </IconButton>
                        </>
                    )}
                </div>
            );
        }

        // Else, show waiting message
        return (
            <>
                <div className={classes.waitingMessageContainer}>
                    <CircularProgress className="icon" />
                    <Typography className="message" variant="h5">{trans(getStreamingStateDisplayName(streamingState))}</Typography>
                </div>
                {/* If state is WAITING_FOR_VIDEO, show the "Cancel for the user to cancel streaming */}
                {streamingState === ActlyzerCameraStreamState.WAITING_FOR_VIDEO && (
                    <Button
                        variant="contained"
                        color="default"
                        size="small"
                        onClick={props.onCancelStreamingRequest}
                    >
                        {trans('label.cancel')}
                    </Button>
                )}
            </>
        );
    }, [classes.controlBar, classes.controlButton, classes.waitingMessageContainer, props, streamingState, trans]);


    return (
        <Card
            className={clsx(props.className, classes.root, `state-${streamingState}`)}
            style={props.style}
            elevation={2}
        >
            <CardMedia className={classes.cardMedia}>
                <div
                    className={clsx(props.className, classes.videoContainer)}
                    style={props.style}
                >
                    {/* Create an overlay on top of the image that the user can click on to play/pause/stop the video */}
                    <div
                        className={classes.controlOverlay}
                    >
                        {createOverlayContent()}
                    </div>
                    <img
                        className={classes.videoImage}
                        src={currentVideoFrame?.image ?? ''}
                        alt={currentVideoFrame?.image ?? ''}
                    />
                </div>
            </CardMedia>
            <CardContent className={classes.cardContent}>
                <div className={classes.cameraInfo}>
                    {props.cameraInfo.streamingState === ActlyzerCameraStreamState.STREAMING ? (
                        <Icon className="camera-icon" fontSize="large">play_arrow</Icon>
                    ) : props.cameraInfo.streamingState === ActlyzerCameraStreamState.ON_PAUSE ? (
                        <Icon className="camera-icon" fontSize="large">pause</Icon>
                    ) : (
                        <Icon className="camera-icon" fontSize="large">stop</Icon>
                    )}
                    <span className="camera-id">
                        {capitalizeAll(trans('label.camera'))}: {props.cameraInfo.cameraId ?? ''}
                    </span>
                </div>

                {currentVideoFrame && (
                    <Typography variant="subtitle1" className={classes.timestamp}>
                        {getDateTimeDisplay(currentVideoFrame.timestamp, trans)}
                    </Typography>
                )}

                {currentVideoFrame && props.showVisitorsCount && (
                    <div className={classes.visitorCountContainer}>
                        <div className="value">{currentVideoFrame.headCount ?? '?'}</div>
                        <div className="label">{trans('video_streaming.visitor.label', { count: 2 })}</div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
